import type {Editor, RichTextEditorAction} from 'grapesjs';
import {renderColorPickerDropdownForText} from '@Global/email-maker';
import {updateColorForColorPicker, updateDoCloseDropdown} from '@Components/email-maker-color-picker-dropdown/email-maker-color-picker-dropdown-reducer';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import type {SelectionSnapshot} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/rte/rte-selection.util';
import {
  copySelectionSnapshot,
  getBooleanFromString,
  getColorFillForCurrentText,
  hasSelectionChanged,
  isCurrentSelectionLink,
} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/rte/rte-selection.util';

/** `editor.RichTextEditor` (the RichTextEditorModule type is not exported). */
type RteManager = Editor['RichTextEditor'];
/** The RTE instance passed to `result`/`update` callbacks (class not exported directly). */
type RteInstance = Parameters<RichTextEditorAction['result']>[0];

/**
 * Mounts the color-picker dropdown into a toolbar icon host and wires Redux selection-tracking /
 * hide-when-link behavior. `lastSnapshotRef` is shared with `updateColorPickerState` so both
 * read/write the same selection snapshot.
 */
export function addColorRteAction(rte: RteManager, lastSnapshotRef: {value: SelectionSnapshot | null}): void {
  rte.add('color', {
    icon: ((): HTMLElement => {
      const colorPickerContainer = document.createElement('div');
      colorPickerContainer.className = 'rte-color-picker-container radius-4';

      const initialSelection = rte.globalRte?.selection();
      const initialColor = getColorFillForCurrentText(initialSelection ?? null);

      renderColorPickerDropdownForText(colorPickerContainer, {
        initialColor,
        onColorChange: (newColor: string): void => {
          rte.globalRte?.exec('foreColor', newColor);
          lastSnapshotRef.value = copySelectionSnapshot(rte.globalRte?.selection() ?? null);
        },
        onColorChangeEnd: (newColor: string): void => {
          rte.globalRte?.exec('foreColor', newColor);
          lastSnapshotRef.value = copySelectionSnapshot(rte.globalRte?.selection() ?? null);
        },
        onOpen: (): void => {
          setColorPickerCTAOpenAttribute(rte, true);
        },
        onClose: (): void => {
          setColorPickerCTAOpenAttribute(rte, false);
          rte.get('color')?.btn?.classList.remove('gjs-rte-active');
        },
        dropdownSelectorClasses: 'rte-color-picker-selector radius-4 border-s-dark',
        popupClassName: 'rte-color-picker-popup',
        popupSpacingClass: 'spacing-p-3',
        alignment: ALIGNMENT_TYPE.BOTTOM_START,
        topOffset: 8,
      });

      return colorPickerContainer;
    })(),

    attributes: {
      title: window.i18next.t('pmwjs_color'),
      class: 'gjs-rte-action',
    },

    // Breaks GrapesJS action registration if removed.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    result: (_rteInstance: RteInstance, _action: RichTextEditorAction) => {},

    update: (rteInstance: RteInstance, action: RichTextEditorAction) => {
      const colorPickerActionCTA = action.btn;
      const currentSelection = rteInstance.selection();

      if (!colorPickerActionCTA || !currentSelection) {
        return 0;
      }

      const isColorPickerOpen = colorPickerActionCTA.getAttribute('data-open') === 'true';
      const selectionChanged = hasSelectionChanged(lastSnapshotRef.value, currentSelection);
      const isCurrentSelectionRange = !currentSelection.isCollapsed;
      const isCurrentRangeSelectionLink = isCurrentSelectionRange && isCurrentSelectionLink(currentSelection);

      if (isColorPickerOpen) {
        action.btn?.classList.add('gjs-rte-active');
      } else {
        action.btn?.classList.remove('gjs-rte-active');
      }

      if (isCurrentRangeSelectionLink) {
        action.btn?.classList.add('_hidden');
        action.btn?.parentElement?.querySelector('.rte-action-divider')?.classList.add('_hidden');
      } else {
        action.btn?.classList.remove('_hidden');
        action.btn?.parentElement?.querySelector('.rte-action-divider')?.classList.remove('_hidden');
      }

      if (selectionChanged) {
        const fillColor = getColorFillForCurrentText(currentSelection);
        window.PMW.redux.store.dispatch(updateColorForColorPicker(fillColor));
        doCloseColorPickerDropDownIfNeeded(colorPickerActionCTA);
      }

      lastSnapshotRef.value = copySelectionSnapshot(rteInstance.selection());
      return 0;
    },
  });
}

export function updateColorPickerState(rte: RteManager, lastSnapshotRef: {value: SelectionSnapshot | null}): void {
  const colorPickerActionCTA = rte.get('color')?.btn;

  if (!colorPickerActionCTA) {
    return;
  }

  const selection = rte.globalRte?.selection();
  lastSnapshotRef.value = copySelectionSnapshot(selection ?? null);
  const color = getColorFillForCurrentText(selection ?? null);

  window.PMW.redux.store.dispatch(updateColorForColorPicker(color));
  doCloseColorPickerDropDownIfNeeded(colorPickerActionCTA);
}

function setColorPickerCTAOpenAttribute(rte: RteManager, isOpen: boolean): void {
  const colorPickerActionCta = rte.get('color')?.btn;
  colorPickerActionCta?.setAttribute('data-open', isOpen.toString());
}

function doCloseColorPickerDropDownIfNeeded(colorPickerCTA: HTMLElement): void {
  const isOpenAttr = colorPickerCTA.getAttribute('data-open');
  if (!isOpenAttr) {
    return;
  }

  const isColorPickerOpen = getBooleanFromString(isOpenAttr);
  if (!isColorPickerOpen) {
    return;
  }

  window.PMW.redux.store.dispatch(updateDoCloseDropdown(true));
}
